import { useState } from 'react';
import { Box, Chip, Collapse, Typography } from '@mui/material';
import {
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  FileDownloadOutlined,
  PlaceOutlined,
} from '@mui/icons-material';
import { CustomButton } from '../../../MUI_custom_component';
import { downloadTreeCertificateAction } from '../APIs/action';
import GlassModal from '../../../MUI_custom_component/Modal/GlassModal';

const GRADIENTS = {
  Birthday:
    'linear-gradient(101deg, rgba(255,255,255,0.5) 0%, rgba(216,247,255,0.6) 36.87%, rgb(223,255,223) 98.31%)',
  Anniversary:
    'linear-gradient(134.28deg, rgba(255,255,255,0.5) 0%, rgba(255,216,228,0.6) 36.865%, rgb(223,230,255) 98.306%)',
};

const CHIP_STYLES = {
  Birthday: { borderColor: 'green', backgroundColor: 'rgba(0,128,0,0.12)' },
  Anniversary: {
    borderColor: '#0081f2',
    backgroundColor: 'rgba(0,129,242,0.12)',
  },
};

const TreePlantedCard = ({
  data,
  isHomePageCard = false,
  isOpen = false,
  isLast = false,
  onToggle,
}) => {
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const occasion = data?.occasion
    ? data.occasion.charAt(0).toUpperCase() +
      data.occasion.slice(1).toLowerCase()
    : 'Birthday';
  const chipStyle = CHIP_STYLES[occasion] || CHIP_STYLES.Birthday;

  return (
    <Box
      sx={{
        background: GRADIENTS[occasion] || GRADIENTS.Birthday,
        borderRadius: '16px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflow: 'hidden',
        mb: isLast ? 0 : 2,
        cursor: 'pointer',
      }}
    >
      {/* Header Row */}
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
        onClick={onToggle}
      >
        {/* Tree Icon */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,165,37,0.2)',
            border: '0.24px solid #FFA525',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <img
            src={data?.logo_url}
            alt='tree icon'
            style={{ width: 28, height: 28, objectFit: 'contain' }}
          />
        </Box>

        {/* Title + Badge + Subtitle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant='h3'
              sx={{ color: '#333', flex: 1, minWidth: 0 }}
            >
              {data?.title}
            </Typography>
            <Chip
              label={occasion}
              size='small'
              variant='outlined'
              sx={{
                ...chipStyle,
                color: '#333',
                fontFamily: 'Lato',
                fontWeight: 600,
                fontSize: '13px',
                height: '24px',
                borderRadius: '32px',
              }}
            />
          </Box>
          <Typography
            variant='p3'
            sx={{ color: '#666' }}
          >
            {data?.description}
          </Typography>
        </Box>

        {/* Chevron */}
        {!isHomePageCard && (
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isOpen ? (
              <KeyboardArrowUpRounded sx={{ color: '#333' }} />
            ) : (
              <KeyboardArrowDownRounded sx={{ color: '#333' }} />
            )}
          </Box>
        )}
      </Box>

      {/* Colapsed state: Tree ID + Planted Date */}
      {!isOpen && (
        <Box sx={{ display: 'flex', gap: 4 }}>
          <Box>
            <Typography
              variant='labelM'
              sx={{ color: '#666' }}
            >
              Tree ID
            </Typography>
            <Typography
              variant='body1'
              sx={{ color: '#333' }}
            >
              {data?.tree_id}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant='labelM'
              sx={{ color: '#666' }}
            >
              Planted Date
            </Typography>
            <Typography
              variant='body1'
              sx={{ color: '#333' }}
            >
              {data?.planted_date
                ? new Date(data.planted_date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '—'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Collapsible Content */}
      {!isHomePageCard && (
        <Collapse
          in={isOpen}
          timeout='auto'
          unmountOnExit
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Planted Tree Image */}
            {data?.planted_image_url && (
              <Box
                component='img'
                src={data.planted_image_url}
                alt='planted tree'
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}

            {/* Colapsed state: Tree ID + Planted Date */}
            {isOpen && (
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography
                    variant='labelM'
                    sx={{ color: '#666' }}
                  >
                    Tree ID
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ color: '#333' }}
                  >
                    {data?.tree_id}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant='labelM'
                    sx={{ color: '#666' }}
                  >
                    Planted Date
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ color: '#333' }}
                  >
                    {data?.planted_date
                      ? new Date(data.planted_date).toLocaleDateString(
                          'en-GB',
                          {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          },
                        )
                      : '—'}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* View Location on Map */}
            <CustomButton
              variant='filled'
              color='primary'
              label='View location on map'
              leftIcon={PlaceOutlined}
              fullWidth
              onClick={() => setMapModalOpen(true)}
            />

            {/* eCertificate */}
            <CustomButton
              variant='text'
              color='primary'
              label='eCertificate'
              leftIcon={FileDownloadOutlined}
              fullWidth
              onClick={() => downloadTreeCertificateAction(data?.tree_id)}
            />
          </Box>
        </Collapse>
      )}

      {isHomePageCard && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <CustomButton
            variant='filled'
            color='primary'
            label='View location on map'
            leftIcon={PlaceOutlined}
            fullWidth
            onClick={() => setMapModalOpen(true)}
          />

          {/* eCertificate */}
          <CustomButton
            variant='text'
            color='primary'
            label='eCertificate'
            leftIcon={FileDownloadOutlined}
            fullWidth
            onClick={() => downloadTreeCertificateAction(data?.tree_id)}
          />
        </Box>
      )}

      <GlassModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        title='Location on Map'
        scrollable={true}
        maxWidth='800px'
        zIndex={2}
        pt={4}
      >
        <Box sx={{ width: '100%', height: { xs: '95%', md: '450px' } }}>
          <iframe
            src={data?.planted_location_url}
            width='100%'
            height='100%'
            style={{ border: 'none', borderRadius: '8px' }}
            title='Planted tree location'
            allowFullScreen
          />
        </Box>
      </GlassModal>
    </Box>
  );
};

export default TreePlantedCard;
